import { useTranscriptionsState } from '@modules/transcriptions/useTranscriptionsState';
import { Badge, Tooltip } from '@mui/material';
import cn from 'classnames';
import { MouseEvent, useMemo } from 'react';
import { Note } from 'src/@types/notes-transcription';
import Iconify from 'src/components/iconify';
import { useSocketEvent } from 'src/socket';
import { transcriptionLoaded } from 'src/socket/listeners/notes-transcription';
import { ActionsBarButtonStyled, StyledListContainer } from '../shared/styles';
import { useActionBarState } from '../useActionBarState';

export const TranscriptionsActionBar = ({
  showBorder,
  onClick,
}: { showBorder: boolean; onClick: () => void }) => {
  const { openAction, setReopen, isOpen, setIsOpen } = useActionBarState();
  const {
    note: openedNote,
    notesList,
    closeNote,
    setNote,
    addNoteToList,
    setActiveTranscriptionChannelId,
    setActiveTranscriptionNote,
    setActiveTranscriptionOwner,
  } = useTranscriptionsState();

  const openedList = useMemo(() => Object.keys(notesList), [notesList]);

  useSocketEvent(
    transcriptionLoaded((socket) => {
      addNoteToList(socket.document);

      if (openedNote?._id === socket.document._id) {
        setIsOpen(false);
        setNote(null);
      }

      setActiveTranscriptionChannelId(null);
      setActiveTranscriptionNote(null);
      setActiveTranscriptionOwner(undefined);
    }),
  );

  const handleOpenNote = (event: MouseEvent<HTMLButtonElement>, note: Note) => {
    event.stopPropagation();
    if (openedNote?._id === note._id) return onClick();
    setNote(note);
    if (isOpen) {
      setReopen('notes');
      setIsOpen(false);
    }
  };

  const handleCloseNote = (note: Note) => {
    closeNote(note);
    if (!openedNote || openedNote._id !== note._id) return;
    setIsOpen(false);
  };

  if (!openedList.length) return null;

  return (
    <StyledListContainer className={cn({ showBorder })}>
      {openedList
        .filter((noteId) => notesList[noteId])
        .map((noteId) => (
          <Tooltip
            title={`#${notesList[noteId].title}`}
            placement="left"
            arrow
            key={noteId}
          >
            <Badge
              onClick={() => handleCloseNote(notesList[noteId])}
              badgeContent="x"
              color="primary"
              sx={{ cursor: 'pointer' }}
            >
              <ActionsBarButtonStyled
                variant="outlined"
                color={
                  openAction === 'notes' &&
                  openedNote?._id === notesList[noteId]._id
                    ? 'primary'
                    : 'inherit'
                }
                onClick={(e) => handleOpenNote(e, notesList[noteId])}
              >
                <Iconify icon="tabler:file-text-spark" />
              </ActionsBarButtonStyled>
            </Badge>
          </Tooltip>
        ))}
    </StyledListContainer>
  );
};
